import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Panel, Dialog, TextInput, Button, IconButton, Notice, ListItem } from '@origo/origo-ui';
import { MagnifyingGlassPlusIcon } from '@phosphor-icons/react';
import type { Område } from '@origo/seksjon';
import { useMapViewContext, selectedPolygonSymbol } from '@origo/map-context';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { useOmråder } from '../../planer/hooks/useOmråder';
import styles from './FlyttOmrådePanel.module.css';

export type MoveTarget =
    | { parentType: 'Delområdetype'; parentId: string; navn: string }
    | { parentType: 'Delområde'; parentId: string; navn: string; delomradetypeId: string; code?: string };

interface Props {
    område: Område;
    planId: string;
    /** FKB delområde-tre (delomradetype → children[]). */
    delomradetypeAreas: Område[];
    onMoved: (target: MoveTarget) => void;
    onCancel: () => void;
}

const HIGHLIGHT_LAYER_ID = 'flytt-omrade-highlight';

export function FlyttOmrådePanel({
    område,
    planId,
    delomradetypeAreas,
    onMoved,
    onCancel,
}: Readonly<Props>) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [target, setTarget] = useState<MoveTarget | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [highlightedIds, setHighlightedIds] = useState<ReadonlySet<string>>(new Set());
    const [ready, setReady] = useState(false);

    const highlightLayerRef = useRef<GraphicsLayer | null>(null);

    const { mapView } = useMapViewContext();
    const { updateOmråde } = useOmråder(planId);

    // Alle delomrader (barn av delomradetypene) som har geometri.
    const allCandidates = useMemo(
        () => delomradetypeAreas.flatMap((g) => g.children ?? []),
        [delomradetypeAreas]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return delomradetypeAreas
            .map((group) => {
                const typeMatches = !q || group.navn.toLowerCase().includes(q);
                const childMatches = (group.children ?? []).filter((child) => {
                    if (!q) return true;
                    return (
                        child.navn.toLowerCase().includes(q) ||
                        (child.code ?? '').toLowerCase().includes(q)
                    );
                });
                return { group, typeMatches, children: childMatches };
            })
            .filter((g) => g.typeMatches || g.children.length > 0);
    }, [delomradetypeAreas, search]);

    // ── GraphicsLayer — livssyklus følger mapView ──
    useEffect(() => {
        const map = mapView?.map;
        if (!mapView || !map) return;

        let destroyed = false;
        const layer = new GraphicsLayer({ id: HIGHLIGHT_LAYER_ID, listMode: 'hide' });
        map.add(layer);
        highlightLayerRef.current = layer;

        mapView.when(() => {
            if (!destroyed) setReady(true);
        });

        return () => {
            destroyed = true;
            setReady(false);
            map.remove(layer);
            layer.destroy();
            highlightLayerRef.current = null;
        };
    }, [mapView]);

    // ── Tegn markering for delomrader ──
    // Hvert delomrade vises med et tynt omriss; valgt target får fylt farge.
    useEffect(() => {
        const layer = highlightLayerRef.current;
        if (!layer || !ready) return;

        layer.removeAll();

        const [r, g, b] = selectedPolygonSymbol().color;

        for (const d of allCandidates) {
            if (!d.geometry) continue;
            const isActive = highlightedIds.size > 0
                ? highlightedIds.has(d.id)
                : d.id === target?.parentId;
            layer.add(new Graphic({
                geometry: d.geometry,
                symbol: (isActive
                    ? {
                        type: 'simple-fill' as const,
                        color: [r, g, b, 0.4],
                        outline: { color: [r, g, b, 1], width: 2 },
                    }
                    : {
                        type: 'simple-fill' as const,
                        color: [r, g, b, 0],
                        outline: { color: [r, g, b, 0.4], width: 1 },
                    }) as never,
            }));
        }
    }, [highlightedIds, target, allCandidates, ready]);

    const handleSelectDelområdetype = useCallback((group: Område) => {
        setHighlightedIds(new Set());
        setTarget(prev =>
            prev?.parentId === group.id ? null :
                { parentType: 'Delområdetype', parentId: group.id, navn: group.navn }
        );
    }, []);

    const handleSelectDelområde = useCallback((child: Område, group: Område) => {
        setHighlightedIds(new Set());
        const label = child.code ?? child.navn;
        setTarget(prev =>
            prev?.parentId === child.id ? null :
                { parentType: 'Delområde', parentId: child.id, navn: label, delomradetypeId: group.id, code: child.code }
        );
    }, []);

    const handleNext = useCallback(() => setConfirmOpen(true), []);

    const handleBack = useCallback(() => {
        setConfirmOpen(false);
        setError(null);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!target) return;
        if (område.objectId == null) {
            setError('Området har ingen objectId og kan ikke flyttes.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await updateOmråde(område.objectId, område.navn, {
                parentType: target.parentType,
                parentId: target.parentId,
            });
            onMoved(target);
        } catch (e) {
            setError((e as Error)?.message ?? 'Kunne ikke flytte området');
            setBusy(false);
        }
    }, [target, område.objectId, område.navn, updateOmråde, onMoved]);

    const zoomTo = useCallback((delområde: Område) => {
        if (!mapView || !delområde.geometry) return;
        setHighlightedIds(new Set([delområde.id]));
        const extent = delområde.geometry.extent;
        const goToTarget = extent ? extent.clone().expand(1.6) : delområde.geometry;
        mapView.goTo(goToTarget, { duration: 400, easing: 'ease-out' }).catch(() => { });
    }, [mapView]);

    const zoomToType = useCallback((group: Område) => {
        const children = (group.children ?? []).filter((c) => c.geometry);
        if (!mapView || children.length === 0) return;
        setHighlightedIds(new Set(children.map((c) => c.id)));
        mapView.goTo(children.map((c) => c.geometry!), { duration: 400, easing: 'ease-out' }).catch(() => { });
    }, [mapView]);

    const targetKindLabel = target?.parentType === 'Delområdetype' ? 'delområdetypen' : 'delområdet';

    const footer = (
        <>
            <Button variant="secondary" onClick={onCancel}>Avbryt</Button>
            <Button variant="primary" onClick={handleNext} disabled={!target}>Neste</Button>
        </>
    );

    return (
        <>
            <Panel heading="Flytt hele området" closable onClose={onCancel} flush footer={footer}>
                <div className={styles.body}>
                    <p className={styles.intro}>
                        Velg ny forelder for &ldquo;{område.navn}&rdquo;. Området beholdes som
                        egendefinert, men arver veiledningspunkter fra den nye forelderen.
                    </p>
                    <div className={styles.search}>
                        <TextInput
                            label="Søk delområdetype eller delområde"
                            placeholder="Filtrer på navn eller kode"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className={styles.list}>
                        {filtered.length === 0 ? (
                            <p className={styles.empty}>Ingen treff på søket.</p>
                        ) : (
                            (() => {
                                const hasSelection = target !== null;
                                return filtered.map(({ group, typeMatches, children }) => {
                                    const isTypeSelected = target?.parentType === 'Delområdetype' && target.parentId === group.id;
                                    return (
                                        <div key={group.id} className={styles.group}>
                                            {typeMatches && (
                                                <div className={[
                                                    styles.optionWrap,
                                                    isTypeSelected ? styles.optionSelected : hasSelection ? styles.optionFaded : '',
                                                ].filter(Boolean).join(' ')}>
                                                    <ListItem
                                                        leading={
                                                            <input
                                                                type="radio"
                                                                aria-label="Velg"
                                                                checked={isTypeSelected}
                                                                onChange={() => { }}
                                                                tabIndex={-1}
                                                                className={styles.radioInput}
                                                            />
                                                        }
                                                        onClick={() => handleSelectDelområdetype(group)}
                                                        className={styles.typeHeader}
                                                    >
                                                        {group.navn}
                                                        <span className={styles.optionHint}> (delområdetype)</span>
                                                    </ListItem>
                                                    <IconButton
                                                        className={styles.zoomButton}
                                                        icon={<MagnifyingGlassPlusIcon size={14} />}
                                                        label={`Zoom til ${group.navn}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => zoomToType(group)}
                                                        disabled={!(group.children ?? []).some((c) => c.geometry)}
                                                    />
                                                </div>
                                            )}
                                            {children.map((child) => {
                                                const isChildSelected = target?.parentType === 'Delområde' && target.parentId === child.id;
                                                return (
                                                    <div
                                                        key={child.id}
                                                        className={[
                                                            styles.optionRow,
                                                            isChildSelected ? styles.optionSelected : hasSelection ? styles.optionFaded : '',
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        <ListItem
                                                            leading={
                                                                <input
                                                                    type="radio"
                                                                    aria-label="Velg"
                                                                    checked={isChildSelected}
                                                                    onChange={() => { }}
                                                                    tabIndex={-1}
                                                                    className={styles.radioInput}
                                                                />
                                                            }
                                                            onClick={() => handleSelectDelområde(child, group)}
                                                            className={styles.optionItem}
                                                        >
                                                            {child.code && <span className={styles.optionCode}>{child.code}</span>}
                                                            {child.navn}
                                                        </ListItem>
                                                        <IconButton
                                                            className={styles.zoomButton}
                                                            icon={<MagnifyingGlassPlusIcon size={14} />}
                                                            label={`Zoom til ${child.navn}`}
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => zoomTo(child)}
                                                            disabled={!child.geometry}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                });
                            })()
                        )}
                    </div>
                </div>
            </Panel>
            <Dialog
                open={confirmOpen}
                onClose={handleBack}
                title="Flytt område"
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={handleBack} disabled={busy}>Tilbake</Button>
                        <Button variant="primary" onClick={handleConfirm} disabled={busy}>
                            {busy ? 'Flytter…' : 'Flytt'}
                        </Button>
                    </div>
                }
            >
                <p className={styles.confirmText}>
                    Vil du flytte <span className={styles.confirmTarget}>&ldquo;{område.navn}&rdquo;</span> til{' '}
                    {targetKindLabel}{' '}
                    <span className={styles.confirmTarget}>&ldquo;{target?.navn}&rdquo;</span>?
                </p>
                {error && <Notice variant="danger">{error}</Notice>}
            </Dialog>
        </>
    );
}
